'use client';

import Script from 'next/script';
import { useEffect } from 'react';
import {
  ShiftDigitalClientId,
  ShiftDigitalProviderId
} from '~/constants/dealership/ShiftDigital';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import ShiftDigitalPageType from '~/constants/dealership/ShiftDigitalPageType';
import { useDealershipConfiguration } from '~/context/dealership/StaticContext';
import type {
  EventPayloads,
  PageViewPayload
} from '~/contracts/dealership/ShiftDigital';

declare global {
  interface Window {
    sd?: {
      (fn: 'config', key: string, value: any): void;
      (
        fn: 'create',
        clientId: string,
        retailerId: string,
        providerId: string
      ): void;
      <K extends ShiftDigitalEventName>(
        fn: 'dataLayer',
        payload: { events: K } & EventPayloads[K]
      ): void;
      <P extends ShiftDigitalPageType>(
        fn: 'dataLayer',
        payload: { pageType: P } & PageViewPayload<P>
      ): void;
      (fn: 'send', eventName?: 'pageview'): void;
      (fn: 'trackLinks'): void;
      q?: any[];
      l?: number;
    };
  }
}

export default function ShiftDigitalScript() {
  const { shiftDigitalRetailerId } = useDealershipConfiguration();

  useEffect(() => {
    // Dev Note: I'm leaving this in so no one else has to dig through the sd.js
    // code to figure out how to enable debug mode
    // window.sd?.('config', 'debug', true);
  }, []);

  return (
    <Script
      type="text/javascript"
      dangerouslySetInnerHTML={{
        __html: `
(() => {
  if (!window.sd) {
    window.sd = (...args) => {
      window.sd.q = window.sd.q ?? [];
      window.sd.q.push(args);
    };

    window.sd.l = Date.now();
  }

  const scriptEl = document.createElement('script');
  scriptEl.async = true;
  scriptEl.src = '//tagging.shiftdigitalapps.io/scripts/$ShiftDigitalClientId/sd.js?containerId=$ShiftDigitalClientId&dl=sd';

  const firstScriptEl = document.getElementsByTagName('script')[0];
  firstScriptEl.parentNode.insertBefore(scriptEl, firstScriptEl);

  window.sd(
    'create',
    '$ShiftDigitalClientId',
    '$ShiftDigitalRetailerId',
    '$ShiftDigitalProviderId'
  );
})();
`
          .replaceAll('$ShiftDigitalClientId', ShiftDigitalClientId)
          .replaceAll('$ShiftDigitalRetailerId', String(shiftDigitalRetailerId))
          .replaceAll('$ShiftDigitalProviderId', ShiftDigitalProviderId)
      }}
    />
  );
}
